import { m, useReducedMotion } from 'framer-motion';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { brand, textures } from '../theme';
import { useCompany } from '../content/ContentProvider';

/**
 * The Google review ask.
 *
 * The five stars are drawn as tiles being laid: square charcoal tiles with a
 * gold star glazed on each, springing into the course one after another the
 * way tile goes onto a wall. It is the one place framer-motion performs on
 * this site, and the gesture belongs to the trade rather than to a template.
 *
 * Two shapes:
 *   <GoogleReview />                 the full band, used on the home page
 *   <GoogleReview variant="inline"/> a compact ask, used after the contact
 *                                    form is sent — the one moment a visitor
 *                                    is demonstrably pleased with us
 *
 * Both links come from the company payload (inc/company.php), so the PHP theme
 * and this app can never hold different URLs.
 */

const STAR_PATH =
	'M12 2.5l2.63 5.86 6.37.66-4.77 4.3 1.35 6.28L12 16.4l-5.58 3.2 1.35-6.28L3 9.02l6.37-.66z';

function StarTile({ index, size }) {
	const reduced = useReducedMotion();

	return (
		<m.span
			initial={reduced ? false : { opacity: 0, y: size * 0.6, rotate: -8, scale: 0.7 }}
			whileInView={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
			viewport={{ once: true, amount: 0.6 }}
			transition={{
				type: 'spring',
				stiffness: 260,
				damping: 18,
				// One tile after another, like a course being laid.
				delay: index * 0.09,
			}}
			style={{
				display: 'grid',
				placeItems: 'center',
				width: size,
				height: size,
				borderRadius: Math.max(3, size * 0.12),
				backgroundColor: brand.darkDeep,
				border: '1px solid rgb(182 142 75 / 45%)',
				boxShadow: '0 4px 12px rgb(31 31 31 / 25%)',
			}}
		>
			<Box component="svg" viewBox="0 0 24 24" sx={{ width: '62%', height: '62%' }} aria-hidden="true">
				<path d={STAR_PATH} fill={brand.accent} />
			</Box>
		</m.span>
	);
}

function Stars({ size = 44 }) {
	return (
		<Box
			aria-hidden="true"
			sx={{ display: 'flex', gap: `${Math.round(size * 0.2)}px`, justifyContent: 'center' }}
		>
			{[0, 1, 2, 3, 4].map((index) => (
				<StarTile key={index} index={index} size={size} />
			))}
		</Box>
	);
}

export default function GoogleReview({ variant = 'band' }) {
	const company = useCompany();

	// Without the URLs there is nothing to ask for.
	if (!company.google_review_url) {
		return null;
	}

	const reviewButton = (
		<Button
			variant="contained"
			color="primary"
			size={variant === 'band' ? 'large' : 'medium'}
			href={company.google_review_url}
			target="_blank"
			rel="noopener noreferrer"
		>
			Review us on Google
		</Button>
	);

	const profileLink = (
		<Box
			component="a"
			href={company.google_profile_url}
			target="_blank"
			rel="noopener noreferrer"
			sx={{
				fontSize: '0.95rem',
				fontWeight: 600,
				color: brand.accentDark,
				textDecoration: 'underline',
				textUnderlineOffset: '3px',
				'&:hover': { color: brand.accent },
			}}
		>
			See our Google profile
		</Box>
	);

	if (variant === 'inline') {
		return (
			<Box sx={{ textAlign: 'center' }}>
				<Stars size={34} />
				<Typography sx={{ mt: 2.5, mb: 3, color: brand.textMuted, maxWidth: '44ch', mx: 'auto' }}>
					Happy with how it went? A short review on Google means a lot to a small crew, and we
					read every one.
				</Typography>
				<Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
					{reviewButton}
					{profileLink}
				</Box>
			</Box>
		);
	}

	return (
		<Box
			component="section"
			aria-labelledby="review-heading"
			sx={{ position: 'relative', overflow: 'hidden', backgroundColor: brand.white, py: { xs: 8, md: 12 } }}
		>
			{/* The grout grid, faint, so even the quiet band is a tiled surface. */}
			<Box
				aria-hidden="true"
				sx={{
					position: 'absolute',
					inset: 0,
					...textures.groutGrid('rgb(45 45 45 / 5%)', '4.5rem'),
					maskImage: 'radial-gradient(ellipse at 50% 0%, #000 0%, transparent 78%)',
					pointerEvents: 'none',
				}}
			/>

			<Container sx={{ position: 'relative', textAlign: 'center' }}>
				<Stars />

				<Typography variant="overline" sx={{ display: 'block', color: brand.accentDark, mt: 4, mb: 1.5 }}>
					Happy with the work?
				</Typography>

				<Typography id="review-heading" variant="h2" component="h2" sx={{ maxWidth: '22ch', mx: 'auto' }}>
					Tell Edmonton About It
				</Typography>

				<Typography sx={{ mt: 2.5, mb: 4, color: brand.textMuted, maxWidth: '50ch', mx: 'auto' }}>
					If we did right by your home, a short review on Google means a lot to a small crew. It
					takes a minute, and we read every single one.
				</Typography>

				<Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
					{reviewButton}
					{profileLink}
				</Box>
			</Container>
		</Box>
	);
}
